import React, { useEffect, useRef } from "react";
import {
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/common/components";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon as MagnifyingGlassIconSolid } from "@heroicons/react/24/solid";
import CourieButton from "@src/common/components/Button/Button";

interface DispatchCandidateSearchPopoverProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
}

export function DispatchCandidateSearchPopover({
  searchTerm,
  setSearchTerm,
}: DispatchCandidateSearchPopoverProps) {
  const hasSearch = searchTerm.length > 0;

  return (
    <Popover placement="bottom-start">
      <PopoverTrigger>
        <CourieButton
          size="sm"
          color={hasSearch ? "success" : "secondary"}
          aria-label="Search dispatch candidates"
          title={hasSearch ? `Searching: "${searchTerm}"` : "Search"}
        >
          {hasSearch ? (
            <MagnifyingGlassIconSolid className="w-4 h-4" />
          ) : (
            <MagnifyingGlassIcon className="w-4 h-4" />
          )}
        </CourieButton>
      </PopoverTrigger>
      <PopoverContent
        className="bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-2xl rounded-md z-50 p-2"
        style={{ zIndex: 9999 }}
      >
        <SearchInputField
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
      </PopoverContent>
    </Popover>
  );
}

function SearchInputField({
  searchTerm,
  setSearchTerm,
}: {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="w-48">
      <Input
        inputRef={inputRef}
        placeholder="Search drivers by name..."
        size="mini"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.currentTarget.value)}
        clearable
        overrides={{
          Root: {
            style: ({ $theme }) => ({
              backgroundColor: $theme.colors.backgroundAlt,
              borderColor: $theme.colors.border,
              borderWidth: "1px",
              borderRadius: $theme.borders.radius400,
              height: "30px",
              boxShadow: "none",
            }),
          },
          ClearIcon: {
            style: {
              width: "24px",
              height: "24px",
            },
            props: {
              size: 24,
            },
          },
          ClearIconContainer: {
            style: {
              backgroundColor: "transparent",
            },
          },
        }}
      />
    </div>
  );
}
